import React, { useEffect, useMemo, useState } from "react";
import { fetchV2 } from "../../utils/apiV2";
import RatingInput, { validarCalificacion } from "./RatingInput";

// Formulario dinámico previsto en docs/arquitectura-objetivo.md §7:
// reemplaza los 23 aspectos hardcodeados de PerformanceEvaluation.js por
// la configuración REAL de un banco (GET api/v2/bancos/{codigo}/vigente),
// aplicando la matriz de habilitación por fuente comportamiento por
// comportamiento — nunca muestra un comportamiento que la fuente actual
// no puede calificar (SourceGate: filtrado por fuentes[fuente]).
//
// No reemplaza ni modifica PerformanceEvaluation.js/PerformanceEvaluationBoss.js
// — esos siguen exactamente igual para el flujo legacy. Este componente es
// nuevo, aislado, y solo se usa desde páginas/rutas que también son
// nuevas (ver frontend/src/pages/Fase2Preview.js).
//
// Este componente NO envía nada al backend por su cuenta — recibe
// `onCambiosValidados` con el estado completo de las calificaciones para
// que quien lo use (una página/contenedor) decida cuándo y cómo guardar
// (vía POST api/v2/asignaciones/{id}/calificaciones, Etapa 7).

/**
 * @param {object} props
 * @param {string} props.codigoBanco Código del banco a cargar, p. ej. "EST", "TEC-L".
 * @param {"auto"|"jefe"|"par"|"colaborador"} props.fuente Fuente desde la que se está calificando — determina qué comportamientos se muestran.
 * @param {(estado: {completo: boolean, totalRequeridos: number, totalRespondidos: number, calificaciones: Array}) => void} [props.onCambio]
 */
export default function CompetencyMatrixForm({ codigoBanco, fuente, onCambio }) {
  const [estadoCarga, setEstadoCarga] = useState("cargando"); // cargando | listo | error | sin-autorizacion
  const [mensajeError, setMensajeError] = useState("");
  const [banco, setBanco] = useState(null);
  const [respuestas, setRespuestas] = useState({}); // id_comportamiento -> {valor, justificacion, evidenciaTexto}

  useEffect(() => {
    let cancelado = false;

    async function cargar() {
      setEstadoCarga("cargando");
      setMensajeError("");

      const { ok, status, data } = await fetchV2(`api/v2/bancos/${encodeURIComponent(codigoBanco)}/vigente`);

      if (cancelado) return;

      if (!ok) {
        if (status === 401 || status === 403) {
          setEstadoCarga("sin-autorizacion");
        } else {
          setEstadoCarga("error");
          setMensajeError((data && data.message) || "No se pudo cargar el banco de competencias.");
        }
        return;
      }

      setBanco(data.data);
      setEstadoCarga("listo");
    }

    if (codigoBanco) {
      cargar();
    }

    return () => {
      cancelado = true;
    };
  }, [codigoBanco]);

  // Comportamientos visibles para la fuente actual (SourceGate) — nunca se
  // muestra uno que fuentes[fuente] no tenga en true.
  const comportamientosVisibles = useMemo(() => {
    if (!banco) return [];
    const lista = [];
    for (const competencia of banco.competencias || []) {
      for (const comportamiento of competencia.comportamientos || []) {
        if (comportamiento.fuentes && comportamiento.fuentes[fuente]) {
          lista.push({ competencia, comportamiento });
        }
      }
    }
    return lista;
  }, [banco, fuente]);

  useEffect(() => {
    if (!onCambio) return;

    let totalRespondidos = 0;
    let hayErrores = false;

    for (const { comportamiento } of comportamientosVisibles) {
      const respuesta = respuestas[comportamiento.id_comportamiento] || {};
      if (respuesta.valor) {
        totalRespondidos += 1;
      }
      if (validarCalificacion(respuesta).length > 0) {
        hayErrores = true;
      }
    }

    const totalRequeridos = comportamientosVisibles.length;

    onCambio({
      completo: totalRequeridos > 0 && totalRespondidos === totalRequeridos && !hayErrores,
      totalRequeridos,
      totalRespondidos,
      calificaciones: comportamientosVisibles.map(({ comportamiento }) => ({
        id_comportamiento: comportamiento.id_comportamiento,
        ...respuestas[comportamiento.id_comportamiento],
      })),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respuestas, comportamientosVisibles]);

  function actualizarRespuesta(idComportamiento, campo, valor) {
    setRespuestas((prev) => ({
      ...prev,
      [idComportamiento]: { ...prev[idComportamiento], [campo]: valor },
    }));
  }

  if (estadoCarga === "cargando") {
    return <div className="fase2-matrix-estado">Cargando banco de competencias…</div>;
  }

  if (estadoCarga === "sin-autorizacion") {
    return (
      <div className="fase2-matrix-estado fase2-matrix-error">
        No tienes autorización para consultar este banco de competencias. Inicia sesión nuevamente.
      </div>
    );
  }

  if (estadoCarga === "error") {
    return <div className="fase2-matrix-estado fase2-matrix-error">{mensajeError}</div>;
  }

  if (comportamientosVisibles.length === 0) {
    return (
      <div className="fase2-matrix-estado">
        No hay ningún comportamiento habilitado para la fuente "{fuente}" en el banco "{codigoBanco}".
      </div>
    );
  }

  // Agrupar por competencia solo para la presentación (el estado sigue plano por comportamiento).
  const competenciasConVisibles = (banco.competencias || [])
    .map((competencia) => ({
      ...competencia,
      comportamientos: (competencia.comportamientos || []).filter(
        (c) => c.fuentes && c.fuentes[fuente]
      ),
    }))
    .filter((competencia) => competencia.comportamientos.length > 0);

  return (
    <div className="fase2-competency-matrix">
      <h3>
        {banco.nombre} <span className="fase2-matrix-codigo">({banco.codigo}, v{banco.version})</span>
      </h3>
      <p className="fase2-matrix-fuente">Calificando como fuente: <strong>{fuente}</strong></p>

      {competenciasConVisibles.map((competencia) => (
        <fieldset key={competencia.id_competencia_cat} className="fase2-competencia-grupo">
          <legend>{competencia.nombre}</legend>
          {competencia.definicion && <p className="fase2-competencia-definicion">{competencia.definicion}</p>}

          {competencia.comportamientos.map((comportamiento) => {
            const respuesta = respuestas[comportamiento.id_comportamiento] || {};
            return (
              <div key={comportamiento.id_comportamiento} className="fase2-comportamiento-fila">
                <p className="fase2-comportamiento-descripcion">{comportamiento.descripcion}</p>
                <RatingInput
                  valor={respuesta.valor || ""}
                  onValorChange={(v) => actualizarRespuesta(comportamiento.id_comportamiento, "valor", v)}
                  justificacion={respuesta.justificacion || ""}
                  onJustificacionChange={(v) =>
                    actualizarRespuesta(comportamiento.id_comportamiento, "justificacion", v)
                  }
                  evidenciaTexto={respuesta.evidenciaTexto || ""}
                  onEvidenciaChange={(v) =>
                    actualizarRespuesta(comportamiento.id_comportamiento, "evidenciaTexto", v)
                  }
                />
              </div>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}
